import { Button, ColorPicker, Form, Input, InputNumber, message, Modal, Popconfirm, Select, Space, Spin, Table } from "antd";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { useEffect, useState } from "react";
import { serverFetch } from "../../server";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import "../company/company.css";
import "./settings-tags.css";

const ColorPickerField = ({ value, onChange }) => (
  <ColorPicker
    format="hex"
    showText
    value={value ?? null}
    onChange={(_, hex) => onChange(hex)}
  />
);

const SettingsTags = () => {
  const isMobile = useViewportBreakpoint() === "mobile";
  const [form] = Form.useForm();
  const [tags, setTags] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingTag, setEditingTag] = useState(null);

  const loadPrograms = () => {
    const progCompany = process.env.REACT_APP_PROG_COMPANY;
    const url = progCompany ? `dict/prog?progCompany=${progCompany}` : "dict/prog";
    serverFetch(url)
      .then((data) => setPrograms(Array.isArray(data) ? data : []))
      .catch(() => setPrograms([]));
  };

  const loadTags = () => {
    setLoading(true);
    serverFetch("Plugin/tags")
      .then((data) => {
        setTags(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка загрузки меток");
      });
  };

  useEffect(() => {
    loadPrograms();
  }, []);

  useEffect(() => {
    loadTags();
  }, []);

  const openAdd = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({ progId: programs[0]?.id, idx: 0 });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingTag(record);
    form.setFieldsValue({
      name: record.name,
      progId: record.progId,
      color: record.color ?? "",
      idx: record.idx ?? 0,
    });
    setModalOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      setLoading(true);
      const payload = {
        name: values.name,
        progId: values.progId,
        color: values.color || null,
        idx: values.idx ?? 0,
      };
      if (editingTag) {
        payload.id = editingTag.id;
        serverFetch("Plugin/tags", { method: "PUT", bodyData: payload })
          .then(() => {
            setLoading(false);
            message.success("Метка обновлена");
            setModalOpen(false);
            loadTags();
          })
          .catch((e) => {
            setLoading(false);
            message.error(e?.userMessage || "Ошибка сохранения");
          });
      } else {
        serverFetch("Plugin/tags", { method: "POST", bodyData: payload })
          .then(() => {
            setLoading(false);
            message.success("Метка добавлена");
            setModalOpen(false);
            loadTags();
          })
          .catch((e) => {
            setLoading(false);
            message.error(e?.userMessage || "Ошибка сохранения");
          });
      }
    });
  };

  const handleDelete = (id) => {
    setLoading(true);
    serverFetch(`Plugin/tags/${id}`, { method: "DELETE" })
      .then(() => {
        setLoading(false);
        message.success("Метка удалена");
        loadTags();
      })
      .catch((e) => {
        setLoading(false);
        message.error(e?.userMessage || "Ошибка удаления");
      });
  };

  const programName = (progId) => programs.find((p) => p.id === progId)?.name ?? progId;

  const handleCardKeyDown = (e, record) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openEdit(record);
    }
  };

  const formModalTitle = "Метка";

  const tagForm = (
    <Form form={form} layout="vertical">
      <Form.Item name="name" label="Название" rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item name="progId" label="Программа" rules={[{ required: true }]}>
        <Select
          options={programs.map((p) => ({ label: p.name, value: p.id }))}
          placeholder="Выберите программу"
          getPopupContainer={isMobile ? () => document.body : undefined}
        />
      </Form.Item>
      <Form.Item name="color" label="Цвет">
        <ColorPickerField />
      </Form.Item>
      <Form.Item name="idx" label="Порядок">
        <InputNumber min={0} style={{ width: "100%" }} />
      </Form.Item>
    </Form>
  );

  const columns = [
    { dataIndex: "id", title: "#", width: 70 },
    { dataIndex: "name", title: "Название" },
    {
      dataIndex: "progId",
      title: "Программа",
      render: (progId) => programName(progId),
    },
    {
      dataIndex: "color",
      title: "Цвет",
      width: 120,
      render: (color) =>
        color ? (
          <span
            style={{
              display: "inline-block",
              width: 20,
              height: 20,
              backgroundColor: color,
              border: "1px solid rgba(0,0,0,0.15)",
              borderRadius: 4,
              verticalAlign: "middle",
            }}
            title={color}
          />
        ) : (
          "—"
        ),
    },
    { dataIndex: "idx", title: "Порядок", width: 90 },
    {
      title: "",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm title="Удалить метку?" onConfirm={() => handleDelete(record.id)} okText="Да" cancelText="Нет">
            <Button type="link" size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div
      className={
        (isMobile ? "settings-tags settings-tags--mobile" : "settings-tags") + " settings-section-root"
      }
    >
      <div className="settings-section-toolbar">
        <Space style={{ marginBottom: 0 }} className={isMobile ? "settings-tags-toolbar--mobile" : undefined}>
        {isMobile ? (
          <>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openAdd}
              aria-label="Добавить"
              className="settings-tags-toolbar-icon-btn"
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={loadTags}
              loading={loading}
              aria-label="Обновить"
              className="settings-tags-toolbar-icon-btn"
            />
          </>
        ) : (
          <>
            <Button type="primary" onClick={openAdd}>
              Добавить
            </Button>
            <Button onClick={loadTags} loading={loading}>
              Обновить
            </Button>
          </>
        )}
        </Space>
      </div>
      <div className="settings-section-scroll">
      {isMobile ? (
        <Spin spinning={loading}>
          <div className="settings-tags-mobile-list" aria-busy={loading}>
            {!loading && tags.length === 0 ? (
              <p style={{ color: "var(--gray-color, #898989)", margin: 0 }}>Нет меток</p>
            ) : (
              tags.map((record) => (
                <div
                  key={record.id}
                  className="settings-tags-mobile-card"
                  onClick={() => openEdit(record)}
                  onKeyDown={(e) => handleCardKeyDown(e, record)}
                  role="button"
                  tabIndex={0}
                >
                  <div
                    className="settings-tags-mobile-card-delete"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    <Popconfirm
                      title="Удалить метку?"
                      onConfirm={() => handleDelete(record.id)}
                      okText="Да"
                      cancelText="Нет"
                    >
                      <Button type="text" size="small" danger icon={<DeleteOutlined />} aria-label="Удалить" />
                    </Popconfirm>
                  </div>
                  <div className="settings-tags-mobile-row1">
                    <span
                      className="settings-tags-mobile-swatch"
                      style={{ backgroundColor: record.color || "#f0f0f0" }}
                      title={record.color || "Без цвета"}
                      aria-hidden
                    />
                    <span className="settings-tags-mobile-name">{record.name ?? "—"}</span>
                  </div>
                  <div className="settings-tags-mobile-row2">
                    <span className="settings-tags-mobile-meta">
                      <strong>#</strong> {record.id}
                    </span>
                    <span className="settings-tags-mobile-meta">
                      <strong>Программа</strong> {programName(record.progId)}
                    </span>
                    <span className="settings-tags-mobile-meta">
                      <strong>Порядок</strong> {record.idx ?? 0}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </Spin>
      ) : (
        <Table
          columns={columns}
          dataSource={tags}
          loading={loading}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: "max-content" }}
        />
      )}
      </div>

      {modalOpen && isMobile ? (
        <div
          className="misoft-modal-overlay company-base-overlay--mobile settings-tags-edit-overlay"
          onClick={e => e.target === e.currentTarget && setModalOpen(false)}
        >
          <div
            className="misoft-modal-content company-base-sheet--mobile settings-tags-edit-panel"
            onClick={e => e.stopPropagation()}
          >
            <div className="company-base-sheet-header">
              <span className="company-base-sheet-title">{formModalTitle}</span>
              <Space size="small" className="company-base-sheet-actions">
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  onClick={handleSave}
                  loading={loading}
                  aria-label="Сохранить"
                  className="company-base-header-icon-btn"
                />
                <Button
                  type="default"
                  icon={<CloseOutlined />}
                  onClick={() => setModalOpen(false)}
                  aria-label="Закрыть"
                  className="company-base-header-icon-btn"
                />
              </Space>
            </div>
            <div className="company-base-sheet-body">{tagForm}</div>
          </div>
        </div>
      ) : null}

      <Modal
        title={formModalTitle}
        open={modalOpen && !isMobile}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        okText="Сохранить"
        cancelText="Отмена"
        destroyOnClose
      >
        {tagForm}
      </Modal>
    </div>
  );
};

export default SettingsTags;
